import { useState, useEffect } from 'react';

/**
 * Manages all user-preference settings stored in localStorage,
 * and listens for cross-component setting change events.
 */
export function useSettings() {
  const [showCompletedTasks, setShowCompletedTasks] = useState(true);
  const [layoutMode, setLayoutMode] = useState('side-by-side');
  const [showDescription, setShowDescription] = useState(true);
  const [moveCompletedToBottom, setMoveCompletedToBottom] = useState(true);
  const [showTaskNumbers, setShowTaskNumbers] = useState(false);
  const [theme, setTheme] = useState('prime');

  useEffect(() => {
    // Load persisted settings
    const saved = localStorage.getItem('showCompletedTasks');
    if (saved !== null) setShowCompletedTasks(saved === 'true');

    const savedLayout = localStorage.getItem('layoutMode');
    if (savedLayout) {
      setLayoutMode(savedLayout);
    } else {
      // Default: priority-stacked on mobile, side-by-side on desktop
      const isMobile = window.innerWidth < 768;
      setLayoutMode(isMobile ? 'priority-stacked' : 'side-by-side');
    }

    const savedDesc = localStorage.getItem('showDescription');
    if (savedDesc !== null) setShowDescription(savedDesc === 'true');

    const savedMove = localStorage.getItem('moveCompletedToBottom');
    if (savedMove !== null) setMoveCompletedToBottom(savedMove === 'true');

    const savedNumbers = localStorage.getItem('showTaskNumbers');
    if (savedNumbers !== null) setShowTaskNumbers(savedNumbers === 'true');

    const savedTheme = localStorage.getItem('theme');
    if (savedTheme) {
      setTheme(savedTheme);
    } else {
      setTheme('prime');
    }

    // Set dark mode as default if not previously set
    const savedDarkMode = localStorage.getItem('darkMode');
    if (savedDarkMode === null) {
      document.documentElement.classList.add('dark');
      localStorage.setItem('darkMode', 'true');
    }

    // Event listeners for cross-component setting changes
    const handlers = {
      settingsChanged: (e) => setShowCompletedTasks(e.detail.showCompletedTasks),
      layoutChanged: (e) => setLayoutMode(e.detail.layoutMode),
      descriptionVisibilityChanged: (e) => setShowDescription(e.detail.showDescription),
      moveCompletedSettingChanged: (e) => setMoveCompletedToBottom(e.detail.moveCompletedToBottom),
      taskNumbersVisibilityChanged: (e) => setShowTaskNumbers(e.detail.showTaskNumbers),
      themeChanged: (e) => setTheme(e.detail.theme),
    };

    Object.entries(handlers).forEach(([event, handler]) => {
      window.addEventListener(event, handler);
    });

    return () => {
      Object.entries(handlers).forEach(([event, handler]) => {
        window.removeEventListener(event, handler);
      });
    };
  }, []);

  return {
    showCompletedTasks,
    layoutMode,
    showDescription,
    moveCompletedToBottom,
    showTaskNumbers,
    theme,
  };
}
